import { useEffect, useRef, useState } from 'react';

// Tweens a numeric value and renders it via `format`.
// - First mount: counts up from 0.
// - Later changes (e.g. live FX refresh): tweens to the new value + briefly pulses.
//   If the value is unchanged (USD canonical), nothing animates.
export default function AnimatedNumber({ value = 0, format = v => v, duration = 650, className = '', style = {} }) {
  const [display, setDisplay] = useState(0);
  const fromRef = useRef(0);
  const rafRef = useRef(0);
  const mountedRef = useRef(false);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const target = Number(value) || 0;
    const from = fromRef.current;
    if (target === from) return;

    // pulse only for live updates, not the initial count-up
    if (mountedRef.current) { setPulse(false); requestAnimationFrame(() => setPulse(true)); }
    mountedRef.current = true;

    const start = performance.now();
    cancelAnimationFrame(rafRef.current);
    const tick = now => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      const cur = from + (target - from) * eased;
      setDisplay(cur);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
      else { fromRef.current = target; setDisplay(target); }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration]);

  return (
    <span className={`${className} ${pulse ? 'num-tick' : ''}`} style={style}
      onAnimationEnd={() => setPulse(false)}>
      {format(display)}
    </span>
  );
}
